/**
 * This module is for starting WebRTC streaming using a KVS Signaling Channel.
 */
import AWS from 'aws-sdk'
import * as KVSWebRTC from 'amazon-kinesis-video-streams-webrtc'
import _unionWith from 'lodash/unionWith';
import _isEqual from 'lodash/isEqual';
import watchRTC from '@testrtc/watchrtc-sdk'
import { Subject } from 'rxjs';
import { WebRTCStats } from '@peermetrics/webrtc-stats'

const viewer = {}
const configStatusSubject = new Subject();
const webRtcStatsSubject = new Subject();

// for monitoring webrtc sessions, configuration is set during RTCPeerConnection object creation
watchRTC.init();

// todo: this should return the same id on the same device upon page refresh or rejoin:
// If a user rejoins a session from the same device, it is advisable to assign him the same peerId
// If a user joins a session from two different devices, it is advisable to assign him different peerId values for each device
// If you have multiple WebRTC peer connections being opened from the same device by the same user then it is advisable to associate them all with the same peerId
function getRandomClientId () {
    return Math.random()
        .toString(36)
        .substring(2)
        .toUpperCase()
}

const accessKeyId = 'REPLACE_ME'
const secretAccessKey = 'REPLACE_ME'

async function startViewer (remoteView, channelARN, channelName, displayName, onClientSpecificMessage,
                            onFileReceive, onTelemetryDataMessage, onIceConnectionStateChange) {
    viewer.remoteView = remoteView

    // Create KVS client
    const kinesisVideoClient = new AWS.KinesisVideo({
        region: 'eu-central-1',
        accessKeyId: accessKeyId,
        secretAccessKey: secretAccessKey,
        sessionToken: null,
        endpoint: null,
    })

    // Get signaling channel ARN (check AWS console to get this information)
    // const describeSignalingChannelResponse = await kinesisVideoClient
    //     .describeSignalingChannel({
    //         ChannelName: 'drone-command-control-poc',
    //     })
    //     .promise()
    // const channelARN = describeSignalingChannelResponse.ChannelInfo.ChannelARN
    console.log('[VIEWER] Channel ARN: ', channelARN)

    // Get signaling channel endpoints
    const getSignalingChannelEndpointResponse = await kinesisVideoClient
        .getSignalingChannelEndpoint({
            ChannelARN: channelARN,
            SingleMasterChannelEndpointConfiguration: {
                Protocols: ['WSS', 'HTTPS'],
                Role: KVSWebRTC.Role.VIEWER,
            },
        })
        .promise()
    const endpointsByProtocol = getSignalingChannelEndpointResponse.ResourceEndpointList.reduce((endpoints, endpoint) => {
        endpoints[endpoint.Protocol] = endpoint.ResourceEndpoint
        return endpoints
    }, {})
    console.log('[VIEWER] Endpoints: ', endpointsByProtocol)

    const kinesisVideoSignalingChannelsClient = new AWS.KinesisVideoSignalingChannels({
        region: 'eu-central-1',
        accessKeyId: accessKeyId,
        secretAccessKey: secretAccessKey,
        sessionToken: null,
        endpoint: endpointsByProtocol.HTTPS,
    })

    // Get ICE server configuration
    const getIceServerConfigResponse = await kinesisVideoSignalingChannelsClient
        .getIceServerConfig({
            ChannelARN: channelARN,
        })
        .promise()
    const iceServers = []

    // NAT Traversal option, if STURN/TURN option is selected
    const natTraversalDisabled = false
    const forceTURN = false

    if (!natTraversalDisabled && !forceTURN) {
        iceServers.push({ urls: 'stun:stun.kinesisvideo.eu-central-1.amazonaws.com:443' })
    }
    if (!natTraversalDisabled) {
        getIceServerConfigResponse.IceServerList.forEach(iceServer =>
            iceServers.push({
                urls: iceServer.Uris,
                username: iceServer.Username,
                credential: iceServer.Password,
            }),
        )
    }
    console.log('[VIEWER] ICE servers: ', iceServers)

    // create client id
    const clientId = getRandomClientId();

    // Create Signaling Client
    viewer.signalingClient = new KVSWebRTC.SignalingClient({
        channelARN,
        channelEndpoint: endpointsByProtocol.WSS,
        clientId: clientId,
        role: KVSWebRTC.Role.VIEWER,
        region: 'eu-central-1',
        credentials: {
            accessKeyId: accessKeyId,
            secretAccessKey: secretAccessKey,
            sessionToken: null,
        },
    })

    const wideScreen = true
    const resolution = wideScreen ? { width: { ideal: 1280 }, height: { ideal: 720 } } : { width: { ideal: 640 }, height: { ideal: 480 } }

    const sendVideo = true
    const sendAudio = false
    // eslint-disable-next-line no-unused-vars
    const constraints = {
        video: sendVideo ? resolution : false,
        audio: sendAudio,
    }
    const configuration = {
        iceServers,
        iceTransportPolicy: forceTURN ? 'relay' : 'all',
        watchrtc: {
            rtcApiKey: '5ea2fb82-f057-4039-8aff-ba9a587b8c53',
            rtcRoomId: channelName, // todo: this is a static room id, but we need a session id unique for each session in particular room. One option is to start sending this unique session as a query param via weblink from master side.
            // todo: We can't generate this unique session id on the client, as it has to be the same for all users. Channel name would then be moved to keys section.
            rtcPeerId: clientId,
            keys: { displayName: displayName } // todo: add events when clients triggers actions such as recording etc.
        }
    }
    viewer.peerConnection = new RTCPeerConnection(configuration)
    const openDataChannel = true

    if (openDataChannel) {
        // local viewer data channel creation, this is handled on Android side via WebRtcConnection.onDataChannel
        // channel for handling all events
        // todo: split handling flight events and config status events to separate channels?
        // todo: rename channel name for better clarification?
        viewer.dataChannel = viewer.peerConnection.createDataChannel('kvsDataChannel')

        // viewer specific messages
        viewer.dataChannel.onmessage = onClientSpecificMessage;

        // todo: create file transfer specific channel (arraybuffer), using onFileReceive as handler for onmessage event

        // telemetry (general) channel messages, this is created on Android side via addGeneralDataChannelToLocalPeer
        viewer.peerConnection.ondatachannel = event => {
            // handler for telemetry data channel
            if (event.channel.label === 'telemetry-data-channel') {
                event.channel.onmessage = onTelemetryDataMessage
            } else if (event.channel.label === 'config-status-channel') {
                event.channel.onmessage = function(event) {
                    const configStatus = JSON.parse(event.data)
                    configStatusSubject.next(configStatus);
                }
            } else {
                // covering older mobile app versions that still use channel name "drone-control-channel-" + masterClientId
                event.channel.onmessage = onTelemetryDataMessage
            }
        }
    }

    // Poll for connection stats
    let webrtcStats = new WebRTCStats({
        getStatsInterval: 2000,
        statsObject: true,
    })
    webrtcStats.addConnection({
        pc: viewer.peerConnection, // RTCPeerConnection instance
        peerId: '1', // any string that helps you identify this peer,
        connectionId: '06d54adc-e478-4f95-9144-bbb3562a2aad', // optional, an id that you can use to keep track of this connection
        // remote: false // optional, override the global remote flag
    })
    webrtcStats.on('stats', (ev) => {
        webRtcStatsSubject.next(ev);
    })

    const useTrickleICE = true

    viewer.signalingClient.on('open', async () => {
        console.log('[VIEWER] Connected to signaling service')

        try {
            // only one way video receiving
            viewer.videoTransceiver = viewer.peerConnection.addTransceiver('video', {direction: 'recvonly'});

            try {
                // try to get user microphone
                let stream = await navigator.mediaDevices.getUserMedia({audio: true})

                // no exception, therefore specify to receive and send audio
                viewer.peerConnection.addTransceiver('audio', {direction: 'sendrecv'});

                // send audio track back to master
                stream.getTracks().forEach(track => viewer.peerConnection.addTrack(track, stream))
            } catch (err) {
                // user declined access to microphone, specify via transceiver to only receive audio
                viewer.peerConnection.addTransceiver('audio', {direction: 'recvonly'});
            }
        } catch (e) {
            console.error('[VIEWER] Transceiver error');
            return;
        }

        // Create an SDP offer to send to the master
        console.log('[VIEWER] Creating SDP offer')
        await viewer.peerConnection.setLocalDescription(
            await viewer.peerConnection.createOffer({
                offerToReceiveAudio: true,
                offerToReceiveVideo: true,
            }),
        )

        // When trickle ICE is enabled, send the offer now and then send ICE candidates as they are generated. Otherwise wait on the ICE candidates.
        if (useTrickleICE) {
            console.log('[VIEWER] Sending SDP offer')
            viewer.signalingClient.sendSdpOffer(viewer.peerConnection.localDescription)
        }
        console.log('[VIEWER] Generating ICE candidates')
    })

    viewer.signalingClient.on('sdpAnswer', async answer => {
        // Add the SDP answer to the peer connection
        console.log('[VIEWER] Received SDP answer')
        await viewer.peerConnection.setRemoteDescription(answer)
    })

    viewer.signalingClient.on('iceCandidate', candidate => {
        // Add the ICE candidate received from the MASTER to the peer connection
        console.log('[VIEWER] Received ICE candidate')
        viewer.peerConnection.addIceCandidate(candidate)
    })

    viewer.signalingClient.on('close', () => {
        console.log('[VIEWER] Disconnected from signaling channel')
    })

    viewer.signalingClient.on('error', error => {
        console.error('[VIEWER] Signaling client error: ', error)
    })

    // Send any ICE candidates to the other peer
    viewer.peerConnection.addEventListener('icecandidate', ({ candidate }) => {
        if (candidate) {
            console.log('[VIEWER] Generated ICE candidate')

            // When trickle ICE is enabled, send the ICE candidates as they are generated.
            if (useTrickleICE) {
                console.log('[VIEWER] Sending ICE candidate')
                viewer.signalingClient.sendIceCandidate(candidate)
            }
        } else {
            console.log('[VIEWER] All ICE candidates have been generated')

            // When trickle ICE is disabled, send the offer now that all the ICE candidates have ben generated.
            if (!useTrickleICE) {
                console.log('[VIEWER] Sending SDP offer')
                viewer.signalingClient.sendSdpOffer(viewer.peerConnection.localDescription)
            }
        }
    })

    // As remote tracks are received, add them to the remote view
    viewer.peerConnection.addEventListener('track', event => {
        console.log('[VIEWER] Received remote track')
        if (viewer.remoteView.srcObject) {
            return
        }
        viewer.remoteStream = event.streams[0]
        viewer.remoteView.srcObject = viewer.remoteStream

        if (viewer.remoteView.paused) {
            viewer.remoteView.play();
        }
    })

    // IceConnection state change event
    // https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/iceconnectionstatechange_event
    viewer.peerConnection.addEventListener('iceconnectionstatechange', onIceConnectionStateChange)

    console.log('[VIEWER] Starting viewer connection')
    viewer.signalingClient.open()
}

// eslint-disable-next-line no-unused-vars
function setVideoCodecPreference() {
    // Propose using VP9 codec as default video codec, fallback to VP8
    // Call this method when VP9 decoding performance improves. Currently VP8 is still faster.
    const senderVideoCapabilities = RTCRtpSender.getCapabilities('video').codecs;
    const receiverVideoCapabilities = RTCRtpReceiver.getCapabilities('video').codecs;

    // compiling a list of codecs compatibility as defined in:
    // https://developer.mozilla.org/en-US/docs/Web/API/RTCRtpTransceiver/setCodecPreferences
    const capabilities = _unionWith(senderVideoCapabilities, receiverVideoCapabilities, _isEqual);

    // put VP8 at the beginning
    capabilities.forEach((codec, idx) => {
        if (codec.mimeType === 'video/VP8') {
          capabilities.splice(idx, 1);
          // move VP9 codec to the top of the list
          capabilities.unshift(codec);
        }
    });

    // put VP9 at the beginning
    capabilities.forEach((codec, idx) => {
        if (codec.mimeType === 'video/VP9') {
          capabilities.splice(idx, 1);
          // move VP9 codec to the top of the list
          capabilities.unshift(codec);
        }
    });

    viewer.videoTransceiver.setCodecPreferences(capabilities);
}

// eslint-disable-next-line no-unused-vars
function stopViewer () {
    console.log('[VIEWER] Stopping viewer connection')
    if (viewer.signalingClient) {
        viewer.signalingClient.close()
        viewer.signalingClient = null
    }

    if (viewer.peerConnection) {
        viewer.peerConnection.close()
        viewer.peerConnection = null
    }

    if (viewer.localStream) {
        viewer.localStream.getTracks().forEach(track => track.stop())
        viewer.localStream = null
    }

    if (viewer.remoteStream) {
        viewer.remoteStream.getTracks().forEach(track => track.stop())
        viewer.remoteStream = null
    }

    if (viewer.peerConnectionStatsInterval) {
        clearInterval(viewer.peerConnectionStatsInterval)
        viewer.peerConnectionStatsInterval = null
    }

    if (viewer.remoteView) {
        viewer.remoteView.srcObject = null
    }

    if (viewer.dataChannel) {
        viewer.dataChannel = null
    }
}

// eslint-disable-next-line no-unused-vars
function sendViewerMessage (message) {
    if (viewer.dataChannel) {
        try {
            viewer.dataChannel.send(message)
        } catch (e) {
            console.error('[VIEWER] Send DataChannel: ', e.toString())
        }
    }
}

export { startViewer, stopViewer, sendViewerMessage, viewer, configStatusSubject, webRtcStatsSubject }
